/**
 * Achievements Data
 * Gamification system with 25+ achievements
 */

import type { Achievement } from '../types/academy';

export const ACHIEVEMENTS: Achievement[] = [
  // LEARNING CATEGORY - Common (5 achievements)
  {
    id: 'first-course',
    name: 'First Steps',
    description: 'Complete your first course',
    icon: '🎓',
    category: 'learning',
    rarity: 'common',
    requirement: 'Complete 1 course',
    points: 10,
    condition: { type: 'courses_completed', target: 1 }
  },
  {
    id: 'five-courses',
    name: 'Knowledge Seeker',
    description: 'Complete 5 courses',
    icon: '📚',
    category: 'learning',
    rarity: 'common',
    requirement: 'Complete 5 courses',
    points: 50,
    condition: { type: 'courses_completed', target: 5 }
  },
  {
    id: 'first-hour',
    name: 'Learning Journey Begins',
    description: 'Watch 60 minutes of content',
    icon: '⏱️',
    category: 'learning',
    rarity: 'common',
    requirement: 'Watch 60 minutes total',
    points: 20,
    condition: { type: 'watch_time', target: 60 }
  },
  {
    id: 'early-bird',
    name: 'Early Bird',
    description: 'Start learning before 8 AM',
    icon: '🌅',
    category: 'learning',
    rarity: 'common',
    requirement: 'Complete a course before 8 AM',
    points: 15,
    condition: { type: 'custom', customCheck: 'early_morning' }
  },
  {
    id: 'night-owl',
    name: 'Night Owl',
    description: 'Study after midnight',
    icon: '🦉',
    category: 'learning',
    rarity: 'common',
    requirement: 'Complete a course after midnight',
    points: 15,
    condition: { type: 'custom', customCheck: 'late_night' }
  },

  // COMPLETION CATEGORY - Rare (8 achievements)
  {
    id: 'ten-courses',
    name: 'Dedicated Learner',
    description: 'Complete 10 courses',
    icon: '🌟',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete 10 courses',
    points: 100,
    condition: { type: 'courses_completed', target: 10 }
  },
  {
    id: 'beginner-path',
    name: 'Crypto Fundamentals Master',
    description: 'Complete the Beginner learning path',
    icon: '🎯',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete Beginner path',
    points: 150,
    condition: { type: 'path_completed', pathId: 'beginner' }
  },
  {
    id: 'trader-path',
    name: 'Trading Specialist',
    description: 'Complete the Trader learning path',
    icon: '📊',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete Trader path',
    points: 200,
    condition: { type: 'path_completed', pathId: 'trader' }
  },
  {
    id: 'analyst-path',
    name: 'Technical Analysis Expert',
    description: 'Complete the Analyst learning path',
    icon: '📈',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete Analyst path',
    points: 200,
    condition: { type: 'path_completed', pathId: 'analyst' }
  },
  {
    id: 'portfolio-path',
    name: 'Portfolio Management Pro',
    description: 'Complete the Portfolio Manager path',
    icon: '💼',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete Portfolio Manager path',
    points: 200,
    condition: { type: 'path_completed', pathId: 'portfolio-manager' }
  },
  {
    id: 'five-hours',
    name: 'Committed Student',
    description: 'Watch 5 hours of content',
    icon: '🎬',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Watch 300 minutes total',
    points: 100,
    condition: { type: 'watch_time', target: 300 }
  },
  {
    id: 'speed-learner',
    name: 'Speed Learner',
    description: 'Complete 3 courses in one day',
    icon: '⚡',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete 3 courses in 24 hours',
    points: 150,
    condition: { type: 'custom', customCheck: 'three_in_day' }
  },
  {
    id: 'weekend-warrior',
    name: 'Weekend Warrior',
    description: 'Complete 5 courses on weekends',
    icon: '🏖️',
    category: 'completion',
    rarity: 'rare',
    requirement: 'Complete 5 courses on Sat/Sun',
    points: 120,
    condition: { type: 'custom', customCheck: 'weekend_courses' }
  },

  // STREAK CATEGORY - Rare (5 achievements)
  {
    id: 'three-day-streak',
    name: 'Consistent Learner',
    description: 'Learn for 3 days in a row',
    icon: '🔥',
    category: 'streak',
    rarity: 'rare',
    requirement: '3 day learning streak',
    points: 75,
    condition: { type: 'streak_days', target: 3 }
  },
  {
    id: 'week-streak',
    name: 'Weekly Dedication',
    description: 'Learn for 7 days in a row',
    icon: '🔥🔥',
    category: 'streak',
    rarity: 'epic',
    requirement: '7 day learning streak',
    points: 200,
    condition: { type: 'streak_days', target: 7 }
  },
  {
    id: 'month-streak',
    name: 'Unstoppable',
    description: 'Learn for 30 days in a row',
    icon: '🔥🔥🔥',
    category: 'streak',
    rarity: 'legendary',
    requirement: '30 day learning streak',
    points: 500,
    condition: { type: 'streak_days', target: 30 }
  },
  {
    id: 'comeback',
    name: 'The Comeback',
    description: 'Return after a 30 day break',
    icon: '↩️',
    category: 'streak',
    rarity: 'rare',
    requirement: 'Return after 30+ days',
    points: 100,
    condition: { type: 'custom', customCheck: 'comeback' }
  },
  {
    id: 'never-missed',
    name: 'Perfect Attendance',
    description: 'Learn every day for a month',
    icon: '💯',
    category: 'streak',
    rarity: 'legendary',
    requirement: '30 consecutive days without missing',
    points: 1000,
    condition: { type: 'streak_days', target: 30 }
  },

  // MILESTONE CATEGORY - Epic/Legendary (7 achievements)
  {
    id: 'advanced-path',
    name: 'Crypto Master',
    description: 'Complete the Advanced learning path',
    icon: '🚀',
    category: 'milestone',
    rarity: 'epic',
    requirement: 'Complete Advanced path',
    points: 500,
    condition: { type: 'path_completed', pathId: 'advanced' }
  },
  {
    id: 'all-paths',
    name: 'Polymath',
    description: 'Complete all learning paths',
    icon: '🏆',
    category: 'milestone',
    rarity: 'legendary',
    requirement: 'Complete all 5 paths',
    points: 1500,
    condition: { type: 'custom', customCheck: 'all_paths' }
  },
  {
    id: 'twenty-five-courses',
    name: 'Scholar',
    description: 'Complete 25 courses',
    icon: '📖',
    category: 'milestone',
    rarity: 'epic',
    requirement: 'Complete 25 courses',
    points: 300,
    condition: { type: 'courses_completed', target: 25 }
  },
  {
    id: 'ten-hours',
    name: 'Time Investor',
    description: 'Watch 10 hours of content',
    icon: '⌛',
    category: 'milestone',
    rarity: 'epic',
    requirement: 'Watch 600 minutes total',
    points: 250,
    condition: { type: 'watch_time', target: 600 }
  },
  {
    id: 'all-courses',
    name: 'Completionist',
    description: 'Complete every available course',
    icon: '💎',
    category: 'milestone',
    rarity: 'legendary',
    requirement: 'Complete all courses in Academy',
    points: 2000,
    condition: { type: 'custom', customCheck: 'all_courses' }
  },
  {
    id: 'hundred-percent',
    name: 'Perfectionist',
    description: 'Get 100% on all quizzes',
    icon: '🎯',
    category: 'milestone',
    rarity: 'legendary',
    requirement: 'Perfect score on all quizzes',
    points: 1000,
    condition: { type: 'custom', customCheck: 'perfect_quizzes' }
  },
  {
    id: 'level-50',
    name: 'Legendary Scholar',
    description: 'Reach level 50',
    icon: '👑',
    category: 'milestone',
    rarity: 'legendary',
    requirement: 'Accumulate enough XP for level 50',
    points: 5000,
    condition: { type: 'custom', customCheck: 'level_50' }
  }
];

// Helper functions
export function getAchievementById(id: string): Achievement | undefined {
  return ACHIEVEMENTS.find(a => a.id === id);
}

export function getAchievementsByCategory(category: string): Achievement[] {
  return ACHIEVEMENTS.filter(a => a.category === category);
}

export function getAchievementsByRarity(rarity: string): Achievement[] {
  return ACHIEVEMENTS.filter(a => a.rarity === rarity);
}

// Calculate user level from points
export function calculateLevel(totalPoints: number): number {
  // Level formula: level = floor(sqrt(points / 100))
  // Level 1: 100 points
  // Level 2: 400 points
  // Level 5: 2500 points
  // Level 10: 10000 points
  return Math.floor(Math.sqrt(totalPoints / 100)) + 1;
}

// Calculate points needed for next level
export function pointsForNextLevel(currentLevel: number): number {
  return (currentLevel ** 2) * 100;
}

// Get rarity color
export function getRarityColor(rarity: string): string {
  switch (rarity) {
    case 'common': return '#9ca3af';
    case 'rare': return '#3b82f6';
    case 'epic': return '#a855f7';
    case 'legendary': return '#f59e0b';
    default: return '#6b7280';
  }
}
